import { useEffect, useRef, useState } from "react";
import { TrendingUp, TrendingDown, RefreshCw } from "lucide-react";
import api from "../lib/api";

export function InkMarkets() {
  const [rows, setRows] = useState([]);
  const [updated, setUpdated] = useState(null);
  const [live, setLive] = useState(true);
  const [err, setErr] = useState("");
  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const timer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/markets");
      const list = data.assets || [];
      setRows(list);
      setLive(data.live !== false);
      setUpdated(data.updated ? new Date(data.updated) : (list.length ? new Date() : null));
      setErr(list.length ? "" : "unavailable");
    } catch { setRows([]); setErr("unavailable"); }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 60000);
    return () => clearInterval(timer.current);
  }, []);

  const fmt = (n) => n >= 1000 ? n.toLocaleString("en-US", { maximumFractionDigits: 0 }) : n.toLocaleString("en-US", { maximumFractionDigits: 2 });

  return (
    <div className="bg-white border border-gray-200 shadow-sm" data-testid="ink-markets">
      <div className="flex items-center justify-between px-5 py-3 bg-pti-black text-white">
        <div className="font-display text-lg font-black tracking-tight">Ink<span className="text-pti-red">Markets</span></div>
        <div className="flex items-center gap-2">
          {rows.length > 0 && (
            <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest" data-testid="markets-status">
              <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-green-400 animate-pulse" : "bg-amber-400"}`} aria-hidden="true" />
              <span className={live ? "text-neutral-300" : "text-amber-400"}>{live ? "Live" : "Delayed"}</span>
            </span>
          )}
          <button onClick={load} data-testid="markets-refresh" aria-label="Refresh markets" className="text-neutral-400 hover:text-white">
            <RefreshCw size={12} />
          </button>
        </div>
      </div>
      <div className="p-2">
        {err === "unavailable" && rows.length === 0 && (
          <div className="px-3 py-8 text-center" role="status" data-testid="markets-unavailable">
            <p className="text-sm font-bold text-pti-black">Market data temporarily unavailable</p>
            <p className="text-xs text-gray-400 mt-1">Live prices will return shortly.</p>
            <button onClick={load} className="mt-3 text-[11px] font-bold uppercase tracking-widest border border-pti-black px-3 py-1.5 hover:bg-pti-black hover:text-white transition-colors">Retry</button>
          </div>
        )}
        {rows.length === 0 && !err && <p className="text-gray-400 text-sm py-6 text-center">Loading markets…</p>}
        <div className="divide-y divide-gray-100">
          {rows.map((a) => {
            const chg = a.price_change_percentage_24h || 0;
            const up = chg >= 0;
            return (
              <div key={a.id} className="flex items-center justify-between px-3 py-2.5" data-testid={`market-${a.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  {a.image ? (
                    <img src={a.image} alt="" className="w-6 h-6" />
                  ) : (
                    <span className={`w-6 h-6 flex items-center justify-center text-[9px] font-black rounded-sm shrink-0 ${a.kind === "index" ? "bg-pti-red text-white" : "bg-pti-black text-white"}`} aria-hidden="true">
                      {(a.symbol || "?").replace(/[^A-Za-z0-9]/g, "").slice(0, 3).toUpperCase()}
                    </span>
                  )}
                  <div className="leading-tight">
                    <div className="text-sm font-bold">{a.symbol.toUpperCase()}</div>
                    <div className="text-[11px] text-gray-400 truncate">{a.name}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-sm font-bold">${fmt(a.current_price)}</div>
                  <div className={`text-[11px] font-bold flex items-center justify-end gap-0.5 ${up ? "text-green-600" : "text-pti-red"}`}>
                    {up ? <TrendingUp size={11} aria-hidden="true" /> : <TrendingDown size={11} aria-hidden="true" />}
                    {up ? "+" : ""}{chg.toFixed(2)}%
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {updated && rows.length > 0 && <div className="text-[10px] text-gray-400 px-3 py-2 border-t border-gray-100">{live ? "Updated" : "Delayed · last updated"} {updated.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })} · {live ? "live data" : "indicative"} · Twelve Data · CoinGecko</div>}
      </div>
    </div>
  );
}
